import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { COUNCIL_REVIEWER_SLUGS, hasSeverityAxis } from '@death-star/shared';
import { useApiQuery } from '../use-api.js';
import { repoLabel } from './calibration-repo-label.js';
import {
  FLOOR_METRIC,
  PER_RUN_FLOOR_METRIC,
  REVIEWER_SILENT_METRIC,
  driftMetricLabel,
  fmtDriftValue,
  fmtDriftDelta,
  fmtDriftRange,
  driftSeverityLabel,
  changePointLabel,
  changePointUrl,
  type DriftSeverity,
  type DriftChangePoint,
} from './calibration-drift-format.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { AnnotationsPanel } from '../components/AnnotationsPanel.js';
import { annotationLines } from '../components/AnnotationMarkers.js';
import { annotationsForReviewer, snapAnnotationMarkers, type Annotation } from '../lib/annotations.js';
import { useAnnotations } from '../lib/use-annotations.js';
import { ORGS } from '../orgs.js';

type Tab = 'overview' | 'reviewer' | 'drift';

interface CouncilPoint {
  date: string;
  total_runs: number;
  total_findings: number;
  pct_substantive: number;
  total_usd: number;
}

// Aggregate per-reviewer anchor outcomes across the calibration window.
// Drives the anchor-failure-rate column (diff-vader#79 Phase 0). Optional
// on the API response for back-compat with deployments that pre-date this
// rollout; the portal treats absence as "no data" and renders the section
// only when at least one reviewer has a non-zero total.
interface ReviewerAnchorAggregate {
  reviewer: string;
  findings_anchored: number;
  findings_dropped_unanchored: number;
  // Per-reason drop split (diff-vader#79). Optional for back-compat with API
  // responses that predate the split — treated as 0 when absent.
  dropped_file_level?: number;
  dropped_not_actionable?: number;
  dropped_outside_diff?: number;
  dropped_malformed?: number;
  dropped_gh_rejected?: number;
  // Runner could not parse the file's `@@` header (diff-vader#272). Excluded
  // from the rate: it is a runner parse failure, not the reviewer's.
  dropped_hunk_unparseable?: number;
  // Adjusted failure rate in [0,1] — excludes file_level, not_actionable and
  // hunk_unparseable.
  // Optional for back-compat; the renderer falls back to dropped/total.
  anchor_failure_rate?: number;
}

interface ReviewerPoint {
  date: string;
  runs: number;
  runs_with_findings: number;
  runs_timeout: number;
  runs_error: number;
  runs_failed?: number;            // Optional for back-compat with API responses written before 2026-05-15.
  total_findings: number;
  substantive_findings: number;
  thumbs_up: number;
  thumbs_down: number;
  // `name` is set by the API when at least one rollup row for this (reviewer,
  // signal_id) carried a signal_name. Absent for taxonomy-less reviewers and
  // for rows predating signal_name emission — UI falls back to `#<id>`.
  per_signal: Record<string, { count: number; substantive: number; name?: string }>;
  // Severity buckets for this day. Optional, and deliberately NOT defaulted to
  // {} — the API preserves absent-vs-empty because they mean different things:
  // absent is "this rollup day predates the counter", {} is "measured, no
  // findings". PerSeverityPanel renders those as different states.
  per_severity?: Record<string, number>;
  // Findings-acted-on counters. Optional for back-compat with API responses
  // written before the metric landed; absent → treat as 0.
  comments_total?: number;
  comments_acted_on?: number;
  // Confirmed false positives: substantive findings a later verifier pass
  // refuted (engineering-plugins#291). Charted as a raw count, not a rate,
  // because it has no honest denominator — only findings on PRs that got
  // another push are ever re-examined. Absent → 0.
  comments_refuted?: number;
}

// Reserved bucket key for findings that carried no severity. Mirrors
// SEVERITY_UNSPECIFIED in packages/api/src/lib/calibration-rollup.ts, which is
// where the bucketing happens; duplicated rather than imported because the
// portal does not depend on the API package.
const SEVERITY_UNSPECIFIED = 'unspecified';

// Display order. Anything not listed sorts after these, alphabetically: the API
// deliberately does not map unknown severity values onto known levels, because
// a surprise bucket is the signal that a reviewer changed its vocabulary. Such
// a bucket must therefore stay visible rather than being folded into "other".
const SEVERITY_ORDER = ['high', 'medium', 'low', SEVERITY_UNSPECIFIED];

const SEVERITY_COLORS: Record<string, string> = {
  high: 'var(--loop-red-100)',
  medium: 'var(--loop-yellow-100)',
  low: 'var(--loop-neutral-200)',
  [SEVERITY_UNSPECIFIED]: 'var(--loop-neutral-100)',
};

/**
 * Per-severity distribution for one reviewer, with the states kept distinct.
 *
 * The whole point of this panel is that an empty severity distribution has
 * three unrelated causes, and collapsing them is what cost the correctness
 * investigation days (rewind-corp/diff-vader#223):
 *
 *  1. The reviewer has no severity axis at all — its findings are binary and it
 *     emits null by design. Renders as "n/a", never as a chart.
 *  2. The reviewer has a scale but the days in range predate the counter, so
 *     nothing was measured. Renders as "not measured".
 *  3. The reviewer has a scale, findings were measured, and every one of them
 *     came back unspecified. That is not a distribution — it is the fingerprint
 *     of a reviewer that is not emitting the field, which is precisely the
 *     failure that made the correctness blocking gate unsatisfiable. Called out
 *     explicitly rather than rendered as a single grey bar.
 */
export function PerSeverityPanel({ reviewer, series }: { reviewer: string; series: ReviewerPoint[] }) {
  const heading = (
    <h3 style={{ fontSize: '0.875rem', fontWeight: 664, marginBottom: 8, color: 'var(--loop-neutral-900)' }}>
      Per-Severity Distribution
    </h3>
  );
  const note = (text: string) => (
    <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem', marginBottom: 24 }}>{text}</p>
  );

  if (!hasSeverityAxis(reviewer)) {
    return (
      <>
        {heading}
        {note(
          'n/a — this reviewer has no severity axis. Its findings are binary, so it emits no severity by design. ' +
            'This is a recorded decision, not missing data.'
        )}
      </>
    );
  }

  // Absent per_severity on every point means the counter did not exist for
  // these days. A point carrying {} counts as measured — see the field comment.
  const measured = series.some(p => p.per_severity !== undefined);
  if (!measured) {
    return (
      <>
        {heading}
        {note('Not measured — the rollup days in this range predate per-severity counters.')}
      </>
    );
  }

  const totals: Record<string, number> = {};
  for (const p of series) {
    for (const [bucket, n] of Object.entries(p.per_severity ?? {})) {
      totals[bucket] = (totals[bucket] ?? 0) + n;
    }
  }
  const grandTotal = Object.values(totals).reduce((s, n) => s + n, 0);
  if (grandTotal === 0) {
    return (
      <>
        {heading}
        {note('No findings in this range, so there is no severity distribution to show.')}
      </>
    );
  }

  const unspecified = totals[SEVERITY_UNSPECIFIED] ?? 0;
  const allUnspecified = unspecified === grandTotal;

  const buckets = Object.keys(totals).sort((a, b) => {
    const ai = SEVERITY_ORDER.indexOf(a);
    const bi = SEVERITY_ORDER.indexOf(b);
    if (ai !== bi) return (ai === -1 ? SEVERITY_ORDER.length : ai) - (bi === -1 ? SEVERITY_ORDER.length : bi);
    return a.localeCompare(b);
  });

  return (
    <>
      {heading}
      {allUnspecified && (
        <p style={{ color: 'var(--loop-red-700)', fontSize: '0.875rem', marginBottom: 8 }}>
          Every finding in this range carried no severity, and this reviewer is supposed to have a scale.
          That is an emission failure, not a distribution — any severity-gated behavior for this reviewer
          is currently unsatisfiable.
        </p>
      )}
      <div className="card" style={{ padding: 0, overflowX: 'auto', marginBottom: 24 }}>
        <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
          <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
            <tr>
              {['Severity', 'Findings', '% of findings'].map(h => (
                <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {buckets.map(bucket => {
              const count = totals[bucket] ?? 0;
              const pct = ((count / grandTotal) * 100).toFixed(1);
              const isUnspecified = bucket === SEVERITY_UNSPECIFIED;
              return (
                <tr key={bucket} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                  <td style={{ padding: '8px 16px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 4,
                        background: SEVERITY_COLORS[bucket] ?? 'var(--loop-neutral-100)',
                        color: 'var(--loop-neutral-900)',
                      }}
                      title={
                        isUnspecified
                          ? 'Findings that carried no severity value — impact was never recorded, which is not the same as impact assessed as low'
                          : undefined
                      }
                    >
                      {isUnspecified ? 'unspecified (not recorded)' : bucket}
                    </span>
                  </td>
                  <td style={{ padding: '8px 16px' }}>{count}</td>
                  <td style={{ padding: '8px 16px' }}>{pct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ReliabilityCell({ runs, runsTimeout, runsError, runsFailed = 0 }: { runs: number; runsTimeout: number; runsError: number; runsFailed?: number }) {
  const total = runs + runsTimeout + runsError + runsFailed;
  if (total === 0) {
    return <span style={{ color: 'var(--loop-neutral-700, #626F86)' }}>—</span>;
  }
  const pct = (runs / total) * 100;
  const color =
    pct >= 95 ? 'var(--loop-green-700, #1F845A)'
    : pct >= 80 ? 'var(--loop-yellow-800, #944F00)'
    : 'var(--loop-red-700, #B82837)';
  return <span style={{ color }}>{pct.toFixed(1)}%</span>;
}

interface DriftAlert {
  reviewer: string;
  // `council_pct_substantive_floor` is the council-wide absolute-floor alarm; it
  // reports `baseline` as the fixed floor rather than a trailing average, and
  // arrives with reviewer '(council-wide)'. `council_blocking_per_run_floor` is
  // its second leg (blocking findings per run), shaped the same way.
  // The `detector_blind_*` rows report that a check COULD NOT RUN rather than a
  // metric moving, so their baseline/current are counts. See COUNT_METRICS in
  // calibration-drift-format.ts.
  // `reviewer_silent` reports a reviewer that emitted nothing across both
  // windows: baseline, current and delta are all a literal 0, and `exposure`
  // carries the only informative number.
  metric: 'findings_per_run' | 'pct_substantive' | 'thumbs_down_ratio' | 'council_pct_substantive_floor'
    | 'council_blocking_per_run_floor' | 'detector_blind_council_sample' | 'detector_blind_findings_per_run'
    | 'reviewer_silent';
  baseline: number;
  current: number;
  delta_pct: number;
  // Runs a zero-valued row was observed over. Set only on 'reviewer_silent';
  // optional so rollups stored before it existed still parse.
  exposure?: number;
  severity: DriftSeverity;
  // Present only on 'expected-post-change' rows: the deliberate reviewer change
  // that explains this drift (death-star#308).
  change_point?: DriftChangePoint;
  org: string;
  repo: string;
  year_week: string;
}

const tabBtnStyle = (active: boolean): CSSProperties => ({
  padding: '6px 16px',
  border: active ? '1px solid var(--loop-brand-600)' : '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  background: active ? 'var(--loop-brand-600)' : 'var(--loop-neutral-0)',
  color: active ? '#fff' : 'var(--loop-neutral-700)',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: active ? 528 : 400,
});

const inputStyle: CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
};

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function weeksAgoStr(weeks: number): string {
  return new Date(Date.now() - weeks * 7 * 86400000).toISOString().slice(0, 10);
}

function previousIsoWeek(): string {
  const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

export function Calibration() {
  const [tab, setTab] = useState<Tab>('overview');
  // null = all orgs (no org= query param); single org name = filter to that org.
  // Defaults to all-orgs per issue #142 — the rewindio-only default was
  // masking signal_name data from rewind-corp/ai-prototypes rollups.
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>
          Calibration
        </h2>
        <ConceptHelpIcon slug="calibration" />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="calibration-org-select" style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>
            Org
          </label>
          <select
            id="calibration-org-select"
            value={selectedOrg ?? ''}
            onChange={e => setSelectedOrg(e.target.value === '' ? null : e.target.value)}
            style={{ ...inputStyle, minWidth: 160 }}
          >
            <option value="">All orgs</option>
            {ORGS.map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>
      <nav style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button style={tabBtnStyle(tab === 'overview')} onClick={() => setTab('overview')}>
          Council Overview
        </button>
        <button style={tabBtnStyle(tab === 'reviewer')} onClick={() => setTab('reviewer')}>
          Per-Reviewer
        </button>
        <button style={tabBtnStyle(tab === 'drift')} onClick={() => setTab('drift')}>
          Drift Alerts
        </button>
      </nav>
      {tab === 'overview' && <CouncilOverviewTab selectedOrg={selectedOrg} />}
      {tab === 'reviewer' && <PerReviewerTab selectedOrg={selectedOrg} />}
      {tab === 'drift' && <DriftAlertsTab selectedOrg={selectedOrg} />}
    </div>
  );
}

function CouncilOverviewTab({ selectedOrg }: { selectedOrg: string | null }) {
  const today = todayStr();
  const twelveWeeksAgo = weeksAgoStr(12);
  // Council view: every annotation, including single-reviewer ones — one
  // reviewer's model change is exactly what explains a council-level step.
  const annotations = annotationsForReviewer(useAnnotations(twelveWeeksAgo, today), null);
  const orgQs = selectedOrg ? `&org=${encodeURIComponent(selectedOrg)}` : '';
  const { data, isLoading, error } = useApiQuery<{
    series: CouncilPoint[];
    reviewer_anchor_status?: ReviewerAnchorAggregate[];
  }>(
    ['calibration', 'council', selectedOrg ?? '*', twelveWeeksAgo, today],
    `/api/calibration/council?from=${twelveWeeksAgo}&to=${today}${orgQs}`
  );

  if (isLoading) return <div style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading council data…</div>;
  if (error) return <div className="error">Error: {error instanceof Error ? error.message : String(error)}</div>;
  if (!data) return <div style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No data</div>;

  const series = data.series ?? [];
  const anchorAggregates = data.reviewer_anchor_status ?? [];
  // Sparse: hide the section until at least one reviewer has reported anchor
  // outcomes. Pre-Phase-0 (no runner-side emission) this stays empty and the
  // section doesn't render — avoids a confusing all-zeros table.
  const hasAnchorData = anchorAggregates.some(
    a => a.findings_anchored + a.findings_dropped_unanchored > 0,
  );

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--loop-space-300)' }}>
        <ChartCard
          title="Findings per PR"
          data={series.map(p => ({ date: p.date, value: p.total_runs > 0 ? p.total_findings / p.total_runs : 0 }))}
          annotations={annotations}
        />
        <ChartCard
          title="% Substantive"
          data={series.map(p => ({ date: p.date, value: +(p.pct_substantive * 100).toFixed(1) }))}
          formatValue={v => `${v}%`}
          annotations={annotations}
        />
        <ChartCard
          title="Daily cost ($)"
          data={series.map(p => ({ date: p.date, value: +p.total_usd.toFixed(2) }))}
          formatValue={v => `$${v}`}
          annotations={annotations}
        />
        <ChartCard
          title="PR runs"
          data={series.map(p => ({ date: p.date, value: p.total_runs }))}
          annotations={annotations}
        />
      </div>
      <AnnotationsPanel annotations={annotations} />
      {hasAnchorData && (
        <div style={{ marginTop: 24 }}>
          <h3 id="anchor-failure-heading" style={{ fontSize: '0.875rem', fontWeight: 664, marginBottom: 8, color: 'var(--loop-neutral-900)' }}>
            Anchor Failure Rate (per reviewer)
          </h3>
          <p style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-700)', marginTop: 0, marginBottom: 8 }}>
            Share of each reviewer&apos;s anchor-targeting findings the runner couldn&apos;t
            anchor to a diff line — i.e. real anchor misses (<code>outside_diff</code>,{' '}
            <code>malformed</code>, <code>gh_rejected</code>). Findings that flag a whole
            file (<code>file_level</code>) or were advisory &ldquo;no concerns&rdquo; noise
            (<code>not_actionable</code>) reach the author another way and are excluded from
            the rate, as are findings in a file whose hunk header the runner couldn&apos;t
            parse (<code>hunk_unparseable</code>). Higher = the reviewer is reasoning about
            unchanged code. See diff-vader#79.
          </p>
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table aria-labelledby="anchor-failure-heading" style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
              <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                <tr>
                  {['Reviewer', 'Anchor Failure Rate', 'Drop breakdown'].map(h => (
                    <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {anchorAggregates.map(row => {
                  const fileLevel = row.dropped_file_level ?? 0;
                  const notActionable = row.dropped_not_actionable ?? 0;
                  const hunkUnparseable = row.dropped_hunk_unparseable ?? 0;
                  // Real anchor-failure drops: total drops minus the non-failure
                  // reasons. Matches the API's anchor_failure_rate numerator.
                  const failures = Math.max(0, row.findings_dropped_unanchored - fileLevel - notActionable - hunkUnparseable);
                  const attempts = row.findings_anchored + failures;
                  // Prefer the API-computed rate (single source of truth); fall back
                  // to the local computation for older API responses.
                  const pct = (row.anchor_failure_rate ?? (attempts > 0 ? failures / attempts : 0)) * 100;
                  // Breakdown of the real-failure reasons, plus a note of what was
                  // excluded so a high raw-drop reviewer with a low rate is explainable.
                  const reasonBits: string[] = [];
                  if ((row.dropped_outside_diff ?? 0) > 0) reasonBits.push(`outside_diff ${row.dropped_outside_diff}`);
                  if ((row.dropped_malformed ?? 0) > 0) reasonBits.push(`malformed ${row.dropped_malformed}`);
                  if ((row.dropped_gh_rejected ?? 0) > 0) reasonBits.push(`gh_rejected ${row.dropped_gh_rejected}`);
                  const excludedBits: string[] = [];
                  if (fileLevel > 0) excludedBits.push(`file_level ${fileLevel}`);
                  if (notActionable > 0) excludedBits.push(`not_actionable ${notActionable}`);
                  if (hunkUnparseable > 0) excludedBits.push(`hunk_unparseable ${hunkUnparseable}`);
                  return (
                    <tr key={row.reviewer} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                      <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.reviewer}</td>
                      <td style={{ padding: '8px 16px' }}>
                        {attempts > 0
                          ? `${pct.toFixed(1)}% (${failures}/${attempts})`
                          : '—'}
                      </td>
                      <td style={{ padding: '8px 16px', fontSize: '0.75rem', color: 'var(--loop-neutral-700)' }}>
                        {reasonBits.length > 0 ? reasonBits.join(' · ') : '—'}
                        {excludedBits.length > 0 && (
                          <span style={{ color: 'var(--loop-neutral-500)' }}>
                            {reasonBits.length > 0 ? ' · ' : ''}excluded: {excludedBits.join(' · ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ChartCard({
  title,
  data,
  formatValue,
  annotations = [],
}: {
  title: string;
  data: Array<{ date: string; value: number }>;
  formatValue?: (v: number) => string;
  annotations?: Annotation[];
}) {
  const markers = snapAnnotationMarkers(annotations, data.map(p => p.date));
  return (
    <div className="card" style={{ padding: 'var(--loop-space-300)' }}>
      <h3 style={{ fontSize: '0.875rem', fontWeight: 664, marginBottom: 12, color: 'var(--loop-neutral-900)' }}>
        {title}
      </h3>
      {data.length === 0 ? (
        <div style={{ color: 'var(--loop-neutral-500)', fontSize: '0.75rem' }}>No data</div>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--loop-neutral-200)" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: 'var(--loop-neutral-600)' }}
              tickFormatter={(d: string) => d.slice(5)}
            />
            <YAxis tick={{ fontSize: 10, fill: 'var(--loop-neutral-600)' }} width={40} />
            <Tooltip
              formatter={(v) => [formatValue ? formatValue(Number(v)) : v, title]}
              // recharts 3 declares labelFormatter twice with different second
              // params (Tooltip.d.ts vs DefaultTooltipContent.d.ts) and
              // intersects them, so the callback must satisfy both. Annotating
              // the label as `string` satisfies neither — it is a ReactNode.
              labelFormatter={(l: ReactNode) => `Date: ${String(l)}`}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--loop-brand-600)"
              strokeWidth={2}
              dot={false}
            />
            {annotationLines(markers)}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function PerReviewerTab({ selectedOrg }: { selectedOrg: string | null }) {
  const [reviewer, setReviewer] = useState<string>(COUNCIL_REVIEWER_SLUGS[0]);
  const today = todayStr();
  const twelveWeeksAgo = weeksAgoStr(12);

  // This reviewer's annotations plus the fleet-wide ('*') ones.
  const annotations = annotationsForReviewer(useAnnotations(twelveWeeksAgo, today), reviewer);
  const orgQs = selectedOrg ? `&org=${encodeURIComponent(selectedOrg)}` : '';
  const { data, isLoading, error } = useApiQuery<{ series: ReviewerPoint[] }>(
    ['calibration', 'reviewer', reviewer, selectedOrg ?? '*', twelveWeeksAgo, today],
    `/api/calibration/reviewer/${encodeURIComponent(reviewer)}?from=${twelveWeeksAgo}&to=${today}${orgQs}`
  );

  const series = data?.series ?? [];
  // Charts plot quality metrics (findings, substantive rate) which are only
  // meaningful on days the reviewer actually ran. Failure-only days have
  // runs === 0 by construction (timeout/error don't increment runs) and
  // would render as misleading zero-valued points. The Daily Activity
  // table below uses the unfiltered series.
  const chartSeries = series.filter(p => p.runs > 0);

  const allSignalIds = Array.from(
    new Set(series.flatMap(p => Object.keys(p.per_signal)))
  ).sort((a, b) => Number(a) - Number(b));

  const maxCount = Math.max(
    1,
    ...series.flatMap(p => Object.values(p.per_signal).map(s => s.count))
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>
          Reviewer
        </label>
        <select
          value={reviewer}
          onChange={e => setReviewer(e.target.value)}
          style={{ ...inputStyle, minWidth: 220 }}
        >
          {COUNCIL_REVIEWER_SLUGS.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {isLoading && <div style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</div>}
      {error && <div className="error">Error: {error instanceof Error ? error.message : String(error)}</div>}

      {!isLoading && !error && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--loop-space-300)', marginBottom: 24 }}>
            <ChartCard
              title="Findings per run"
              data={chartSeries.map(p => ({
                date: p.date,
                value: p.runs > 0 ? +(p.total_findings / p.runs).toFixed(2) : 0,
              }))}
              annotations={annotations}
            />
            <ChartCard
              title="% Substantive"
              data={chartSeries.map(p => ({
                date: p.date,
                value: p.total_findings > 0 ? +(p.substantive_findings / p.total_findings * 100).toFixed(1) : 0,
              }))}
              formatValue={v => `${v}%`}
              annotations={annotations}
            />
            <ChartCard
              title="Thumbs down ratio"
              data={chartSeries.map(p => ({
                date: p.date,
                value: (p.thumbs_up + p.thumbs_down) > 0
                  ? +(p.thumbs_down / (p.thumbs_up + p.thumbs_down) * 100).toFixed(1)
                  : 0,
              }))}
              formatValue={v => `${v}%`}
              annotations={annotations}
            />
            <ChartCard
              title="% Acted-on"
              data={chartSeries.map(p => {
                const total = p.comments_total ?? 0;
                const acted = p.comments_acted_on ?? 0;
                return {
                  date: p.date,
                  value: total > 0 ? +(acted / total * 100).toFixed(1) : 0,
                };
              })}
              formatValue={v => `${v}%`}
              annotations={annotations}
            />
            {/* A count, not a percentage, and the only card here that is one.
                Every other metric on this page has a denominator that means
                something; this one does not — a finding is re-examined only if
                its PR gets another push, so a rate would read as precision and
                would improve whenever PRs merge faster. See
                PerReviewerRollup.comments_refuted. */}
            <ChartCard
              title="Refuted findings"
              data={chartSeries.map(p => ({ date: p.date, value: p.comments_refuted ?? 0 }))}
              formatValue={v => `${v}`}
              annotations={annotations}
            />
          </div>

          {series.length > 0 && (
            <>
              <h3 id="daily-activity-heading" style={{ fontSize: '0.875rem', fontWeight: 664, marginBottom: 8, color: 'var(--loop-neutral-900)' }}>
                Daily Activity
              </h3>
              <div className="card" style={{ padding: 0, overflowX: 'auto', marginBottom: 24 }}>
                <table aria-labelledby="daily-activity-heading" style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                  <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                    <tr>
                      {['Date', 'Runs', 'Reliability', 'Timeouts', 'Errors', 'Failures', 'Findings'].map(h => (
                        <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {series.map(point => (
                      <tr key={point.date} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                        <td style={{ padding: '8px 16px' }}>{point.date}</td>
                        <td style={{ padding: '8px 16px' }}>{point.runs}</td>
                        <td style={{ padding: '8px 16px' }}>
                          <ReliabilityCell runs={point.runs} runsTimeout={point.runs_timeout} runsError={point.runs_error} runsFailed={point.runs_failed ?? 0} />
                        </td>
                        <td style={{ padding: '8px 16px' }}>{point.runs_timeout}</td>
                        <td style={{ padding: '8px 16px' }}>{point.runs_error}</td>
                        <td style={{ padding: '8px 16px' }}>{point.runs_failed ?? 0}</td>
                        <td style={{ padding: '8px 16px' }}>{point.total_findings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {allSignalIds.length > 0 && (
            <>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 664, marginBottom: 12, color: 'var(--loop-neutral-900)' }}>
                Per-Signal Activity
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `220px repeat(${series.length}, minmax(32px, 1fr))`,
                  gap: 2,
                  marginBottom: 24,
                  overflowX: 'auto',
                }}
              >
                <div style={{ fontSize: '0.625rem', color: 'var(--loop-neutral-500)' }}>Signal</div>
                {series.map(p => (
                  <div key={p.date} style={{ fontSize: '0.625rem', color: 'var(--loop-neutral-500)', textAlign: 'center' }}>
                    {p.date.slice(5)}
                  </div>
                ))}
                {allSignalIds.map(sig => {
                  // First non-empty name across the series for this signal — falls
                  // back to '#<id>' for reviewers that emit signal_id without a
                  // paired name and for daily rows written before signal_name
                  // landed. Title always carries the numeric id for unambiguous
                  // hover context.
                  const name = series.find(p => p.per_signal[sig]?.name)?.per_signal[sig]?.name;
                  const rowLabel = name ? `${name} (#${sig})` : `#${sig}`;
                  return (
                    <Fragment key={sig}>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--loop-neutral-700)',
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={name ? `Signal #${sig}: ${name}` : `Signal #${sig}`}
                      >
                        {rowLabel}
                      </div>
                      {series.map(p => {
                        const count = p.per_signal[sig]?.count ?? 0;
                        // Linear scale — works for moderately distributed signal counts. Switch
                        // to logarithmic if real-world data turns out to be spike-heavy (one
                        // signal firing 100x more than others washes out the median).
                        const intensity = count / maxCount;
                        const bg = count === 0
                          ? 'var(--loop-neutral-100)'
                          : `color-mix(in srgb, var(--loop-brand-600) ${Math.round(intensity * 100)}%, white)`;
                        return (
                          <div
                            key={`cell-${sig}-${p.date}`}
                            title={`${rowLabel} on ${p.date}: ${count} findings`}
                            style={{
                              background: bg,
                              borderRadius: 2,
                              height: 20,
                            }}
                          />
                        );
                      })}
                    </Fragment>
                  );
                })}
              </div>

              <h3 style={{ fontSize: '0.875rem', fontWeight: 664, marginBottom: 8, color: 'var(--loop-neutral-900)' }}>
                Per-Signal Totals
              </h3>
              <div className="card" style={{ padding: 0, overflowX: 'auto', marginBottom: 24 }}>
                <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                  <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                    <tr>
                      {['#', 'Signal', 'Count', '% Substantive'].map(h => (
                        <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allSignalIds.map(sig => {
                      const totCount = series.reduce((s, p) => s + (p.per_signal[sig]?.count ?? 0), 0);
                      const totSubst = series.reduce((s, p) => s + (p.per_signal[sig]?.substantive ?? 0), 0);
                      const pct = totCount > 0 ? (totSubst / totCount * 100).toFixed(1) : '—';
                      // First non-empty name across the series — '—' when the
                      // reviewer emits signal_id without a paired name, or when
                      // the daily rollup pre-dates signal_name emission.
                      const name = series.find(p => p.per_signal[sig]?.name)?.per_signal[sig]?.name;
                      return (
                        <tr key={sig} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                          <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-500)' }}>{sig}</td>
                          <td style={{ padding: '8px 16px' }}>{name ?? <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>}</td>
                          <td style={{ padding: '8px 16px' }}>{totCount}</td>
                          <td style={{ padding: '8px 16px' }}>{totCount > 0 ? `${pct}%` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {allSignalIds.length === 0 && series.length > 0 && (
            <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>
              This reviewer does not emit per-signal data.
            </p>
          )}

          {/* Rendered for every reviewer, including those with no severity
              axis — the "n/a" state is the panel's whole reason for existing,
              so hiding it for exactly the reviewers it describes would put the
              ambiguity back. */}
          <PerSeverityPanel reviewer={reviewer} series={series} />
        </>
      )}
    </div>
  );
}

function DriftAlertsTab({ selectedOrg }: { selectedOrg: string | null }) {
  // Default to the previous ISO week (most recent fully-aggregated week).
  // User can pick any week via the native <input type="week"> below — same
  // pattern as the Digest page.
  const [week, setWeek] = useState<string>(previousIsoWeek());
  const orgQs = selectedOrg ? `&org=${encodeURIComponent(selectedOrg)}` : '';
  const { data, isLoading, error } = useApiQuery<{ alerts: DriftAlert[]; year_week: string }>(
    ['calibration', 'drift', selectedOrg ?? '*', week],
    `/api/calibration/drift?year_week=${week}${orgQs}`
  );

  const alerts = data?.alerts ?? [];

  function openIssueUrl(alert: DriftAlert): string {
    const title = encodeURIComponent(
      `[Calibration Drift] ${alert.reviewer} — ${alert.metric} — ${alert.year_week}`
    );
    const body = encodeURIComponent(
      `## Drift Alert\n\n` +
      `**Reviewer:** ${alert.reviewer}\n` +
      `**Metric:** ${alert.metric}\n` +
      `**Week:** ${alert.year_week}\n` +
      `**Repo:** ${alert.org}/${repoLabel(alert.repo)}\n` +
      // Unit-aware, same as the table: a raw toFixed(4) here would put
      // "Baseline: 0.6000 / Delta: -38.9%" into a hand-filed issue, which is the
      // misreading this whole formatter exists to prevent — and an issue body
      // outlives the dashboard view it was copied from.
      // A silent reviewer's baseline and current are both a literal 0, so the
      // two lines below would read "Baseline: 0 / Current: 0" and lose the only
      // fact the row carries. Report the exposure instead.
      (alert.metric === REVIEWER_SILENT_METRIC
        ? `**Observed:** ${fmtDriftRange(alert.metric, alert.baseline, alert.current, alert.exposure)}\n`
        : (alert.metric === FLOOR_METRIC || alert.metric === PER_RUN_FLOOR_METRIC
            ? `**Floor:** ${fmtDriftValue(alert.metric, alert.baseline)}\n`
            : `**Baseline:** ${fmtDriftValue(alert.metric, alert.baseline)}\n`) +
          `**Current:** ${fmtDriftValue(alert.metric, alert.current)}\n`) +
      `**Delta:** ${fmtDriftDelta(alert.metric, alert.delta_pct)}\n` +
      `**Severity:** ${driftSeverityLabel(alert.severity)}\n` +
      // The cause travels with the copy. An issue body outlives the dashboard
      // view, and an expected-post-change row pasted WITHOUT its change point
      // reads as unexplained drift — the exact misfiling this feature prevents.
      (alert.change_point
        ? `**Explained by:** ${changePointLabel(alert.change_point)} — ${changePointUrl(alert.change_point)}\n`
        : '')
    );
    return `https://github.com/rewind-corp/diff-vader/issues/new?title=${title}&body=${body}`;
  }

  // Three classes, three treatments. 'expected-post-change' gets NEUTRAL rather
  // than a paler alarm colour: it is not a lesser warning, it is a row whose cause
  // is already known. Before this it fell through to the yellow warning branch,
  // which said the opposite of what the class means.
  const SEVERITY_COLORS: Record<DriftSeverity, { bg: string; fg: string; border: string }> = {
    critical: { bg: 'var(--loop-red-100)', fg: 'var(--loop-red-700)', border: 'var(--loop-red-200)' },
    warning: { bg: 'var(--loop-yellow-100)', fg: 'var(--loop-yellow-800)', border: 'var(--loop-yellow-800)' },
    'expected-post-change': { bg: 'var(--loop-neutral-100)', fg: 'var(--loop-neutral-700)', border: 'var(--loop-neutral-300)' },
  };

  const severityBadgeStyle = (severity: DriftSeverity): CSSProperties => {
    const c = SEVERITY_COLORS[severity] ?? SEVERITY_COLORS.warning;
    return {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 'var(--loop-radius-xs)',
      fontSize: '0.75rem',
      fontWeight: 528,
      background: c.bg,
      color: c.fg,
      border: `1px solid ${c.border}`,
      whiteSpace: 'nowrap',
    };
  };

  const header = (
    <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
      <div>
        <label style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>Week</label>
        <input
          type="week"
          value={week}
          onChange={e => setWeek(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--loop-neutral-300)',
            borderRadius: 'var(--loop-radius-sm)',
            fontSize: '0.875rem',
            color: 'var(--loop-neutral-1100)',
            background: 'var(--loop-neutral-0)',
          }}
        />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div>
        {header}
        <div style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading drift data…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        {header}
        <div className="error">Error: {error instanceof Error ? error.message : String(error)}</div>
      </div>
    );
  }
  if (alerts.length === 0) {
    return (
      <div>
        {header}
        <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>No drift alerts for this week.</p>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
          <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
            <tr>
              {['Reviewer', 'Repo', 'Metric', 'Baseline → Current', 'Delta', 'Severity', ''].map(h => (
                <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {alerts.map((a, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.reviewer}</td>
                <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {selectedOrg ? repoLabel(a.repo) : `${a.org}/${repoLabel(a.repo)}`}
                </td>
                <td style={{ padding: '8px 16px' }}>{driftMetricLabel(a.metric)}</td>
                <td style={{ padding: '8px 16px' }}>
                  {fmtDriftRange(a.metric, a.baseline, a.current, a.exposure)}
                </td>
                <td style={{ padding: '8px 16px', color: a.delta_pct > 0 ? 'var(--loop-red-700)' : 'var(--loop-green-700)' }}>
                  {fmtDriftDelta(a.metric, a.delta_pct)}
                </td>
                <td style={{ padding: '8px 16px' }}>
                  <span style={severityBadgeStyle(a.severity)}>{driftSeverityLabel(a.severity)}</span>
                  {a.change_point && (
                    <a
                      href={changePointUrl(a.change_point)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={a.change_point.subject}
                      style={{
                        display: 'block',
                        marginTop: 4,
                        fontSize: '0.7rem',
                        color: 'var(--loop-brand-600)',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {changePointLabel(a.change_point)}
                    </a>
                  )}
                </td>
                <td style={{ padding: '8px 16px' }}>
                  <a
                    href={openIssueUrl(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--loop-brand-600)',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Open GitHub issue
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
